import React, { useEffect, useState } from 'react';
import {
  Box,
  HeadingText,
  GDSStyleObject,
  FormField,
  TextInput,
  StackLayout,
  UtilityText,
} from '@leagueplatform/genesis-core';
import {
  isEmpty,
  isNotEmpty,
  updateFormData,
  isNoSpace,
  isAlphaNumbericWithSpace,
} from '../../../helpers';

export function RequestedDrug(props: any) {
  const { errorHandling, nextButtonClicked, footerToBottom } = props;
  const [form, setForm] = useState({
    drugName: '',
    strength: '',
    quantity: '',
    explainYourRequest: '',
  });
  const [errors, setErrors] = useState({
    drugName: false,
  });

  const formField =
    isEmpty(form.drugName) || !isAlphaNumbericWithSpace(form.drugName);

  const isValid = isNotEmpty(form.drugName);

  useEffect(() => {
    footerToBottom(false);
  }, [footerToBottom]);

  useEffect(() => {
    if (formField) {
      errorHandling(false);
    } else {
      errorHandling(true);
    }
  }, [formField, errorHandling]);

  useEffect(() => {
    if (nextButtonClicked) {
      if (!isValid) {
        setErrors({
          drugName:
            isEmpty(form.drugName) || !isAlphaNumbericWithSpace(form.drugName),
        });
      }
    }
  }, [nextButtonClicked, isValid, form]);

  const updateErrorState = (value: string, id: string) => {
    const nextErrorState = {
      ...errors,
      [id]: isEmpty(value),
    };
    setErrors(nextErrorState);
  };

  const onUpdateField = (event: any) => {
    const { value, id } = event.target;
    if (id === 'quantity') {
      setForm((prevState) => ({
        ...prevState,
        quantity: isNoSpace(value) ? '' : value?.replace(/[^0-9]+/gi, ''),
      }));
    } else {
      setForm((prevState) => ({
        ...prevState,
        [id]: isNoSpace(value) ? '' : value?.replace(/[^a-zA-Z0-9 ]+/gi, ''),
      }));
    }
    updateErrorState(value, id);
  };

  useEffect(() => {
    const drugInfo = JSON.parse(sessionStorage.getItem('formData') || '{}');
    if (drugInfo) {
      setForm({
        drugName: drugInfo?.drugName,
        strength: drugInfo?.strength,
        quantity: drugInfo?.quantity,
        explainYourRequest: drugInfo?.explainYourRequest,
      });
    }
  }, []);

  useEffect(() => {
    if (form) {
      updateFormData(form);
    }
  }, [form]);

  const onBlurField = (event: any) => {
    updateErrorState(event.target.value, event.target.id);
  };

  const onKeyPressField = (event: any) => {
    updateErrorState(event.target.value, event.target.id);
  };

  return (
    <StackLayout
      data-testid="requested-drug"
      horizontalAlignment="center"
      css={
        {
          backgroundColor: 'White',
          maxWidth: '800px',
        } as GDSStyleObject
      }
    >
      <Box
        css={
          {
            display: 'flex',
            flexDirection: 'column',
            padding: '$three $four',
          } as GDSStyleObject
        }
      >
        <HeadingText
          level="1"
          size={{
            '@initial': 'xxl',
            '@mobile': 'lg',
            '@mobileLandscape': 'lg',
            '@tablet': 'xl',
            '@laptop': 'xxl',
            '@desktop': 'xxl',
          }}
        >
          Step Therapy Exception Request Form
        </HeadingText>
        <Box
          css={
            {
              marginTop: '$two',
              marginBottom: '$one',
            } as GDSStyleObject
          }
        >
          <HeadingText
            level="3"
            size={{
              '@initial': 'xl',
              '@mobile': 'lg',
              '@mobileLandscape': 'lg',
              '@tablet': 'xl',
              '@laptop': 'xl',
              '@desktop': 'xl',
            }}
          >
            Step 3: Requested Drug
          </HeadingText>
        </Box>
        <Box>
          <Box
            css={
              {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '$one $none $none $one',
              } as GDSStyleObject
            }
          >
            <FormField
              id="drugName"
              label="Drug Name"
              statusIconLabel="Error"
              inputStatus={errors.drugName ? 'error' : undefined}
              statusMessage={errors.drugName ? 'Drug name is required.' : ''}
            >
              <TextInput
                name="drugName"
                value={form.drugName}
                onChange={onUpdateField}
                onBlur={onBlurField}
                onKeyPress={onKeyPressField}
                maxLength={100}
              />
            </FormField>
          </Box>
          <Box
            css={
              {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '$one $none $none $one',
              } as GDSStyleObject
            }
          >
            <FormField id="strength" label="Strength (Optional)">
              <TextInput
                name="strength"
                value={form.strength}
                onChange={onUpdateField}
                onBlur={onBlurField}
                onKeyPress={onKeyPressField}
                maxLength={25}
              />
            </FormField>
          </Box>
          <Box
            css={
              {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '$one $none $none $one',
              } as GDSStyleObject
            }
          >
            <FormField id="quantity" label="Quantity (Optional)">
              <TextInput
                name="quantity"
                value={form.quantity}
                onChange={onUpdateField}
                onBlur={onBlurField}
                onKeyPress={onKeyPressField}
                maxLength={5}
              />
            </FormField>
          </Box>
          <Box
            css={
              {
                padding: '$one $none $none $one',
              } as GDSStyleObject
            }
          >
            <FormField
              id="explainYourRequest"
              label="Explain Your Request (Optional)"
            >
              <textarea
                rows={10}
                cols={65}
                maxLength={250}
                name="explainYourRequest"
                value={form.explainYourRequest}
                onChange={onUpdateField}
                onBlur={onBlurField}
                onKeyPress={onKeyPressField}
                style={{
                  resize: 'none',
                  fontSize: '14px',
                  fontWeight: '400px',
                  fontFamily:
                    "BlinkMacSystemFont,-apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                  lineHeight: '20px',
                  letterSpacing: 0,
                  borderRadius: '6px',
                  padding: '12px 16px',
                  color: '#2C3135',
                  border: '1px solid #949494',
                  boxSizing: 'border-box',
                  width: '100%',
                }}
              />
            </FormField>
            <UtilityText
              css={
                {
                  fontStyle: 'italic',
                  fontSize: '14px',
                  float: 'right',
                  fontFamily:
                    "BlinkMacSystemFont,-apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                  color: '#2C3135',
                } as GDSStyleObject
              }
            >
              {form.explainYourRequest ? form.explainYourRequest.length : 0}
              /250
            </UtilityText>
          </Box>
        </Box>
      </Box>
    </StackLayout>
  );
}
